import { createSlice,
         createAsyncThunk,
         PayloadAction,
         createSelector       } from '@reduxjs/toolkit';

import Api,
       { ApiError             } from 'src/services/api';
import * as types               from 'src/services/api/types';
import Util                     from 'src/services/util';

import { RootState            } from 'src/store';
import { authCommonSelectors  } from 'src/store/auth/common';
import { authSMSCodeSelectors } from 'src/store/auth/sms-code';

import { isPhone              } from 'src/validators';



const SLICE_NAME = 'auth:phone-enter';

export interface AuthPhoneEnterState {
  phone: string;
  phoneError: string;
  password: string;
}

const initialState: AuthPhoneEnterState = {
  phone: '',
  phoneError: '',
  password: '',
}

// #region keyLoginAsync
type KeyLoginArgs = void;
type KeyLoginResolve = types.auth.PostAuthKeyLoginOut;
export type KeyLoginReject = {
  reason: 'validation' | 'api';
  validationErrors?: {
    phoneError?: string;
  };
  apiError?: ApiError;
}
export const keyLoginAsync = createAsyncThunk<
  KeyLoginResolve,
  KeyLoginArgs,
  {
    state: RootState,
    rejectValue: KeyLoginReject
  }
>(
  `${SLICE_NAME}/keyLoginAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const phone = selectPhone(state);
    const password = selectPassword(state);
    const phoneError = isPhone(phone).error ?? '';

    if (phoneError.length > 0)
    {
      return thunkAPI.rejectWithValue({
        reason: 'validation',
        validationErrors: {
          ...(phoneError.length > 0 ? { phoneError } : {}),
        }
      });
    }

    try
    {
      return await Api.postAuthKeyLogin({
        phone: parseInt(Util.selectDigitsFromString(phone), 10),
        password: password.trim(),
      });
    }
    catch (error)
    {
      return thunkAPI.rejectWithValue({
        reason: 'api',
        apiError: (error as ApiError),
      })
    }
  }
);
// #endregion

// #region loginAsync
type LoginArgs = void;
type LoginResolve = types.auth.AccountWithTokens;
export type LoginReject = {
  reason: 'api';
  apiError?: ApiError;
}
export const loginAsync = createAsyncThunk<
  LoginResolve,
  LoginArgs,
  {
    state: RootState,
    rejectValue: LoginReject
  }
>(
  `${SLICE_NAME}/loginAsync`,
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const authKey = authCommonSelectors.selectAuthKey(state);
    const code2fa = parseInt(authSMSCodeSelectors.selectSMSCode(state), 10);

    try
    {
      return await Api.postAuthLogin({
        authKey,
        code2fa,
        pushToken: '',
      });
    }
    catch (error)
    {
      return thunkAPI.rejectWithValue({
        reason: 'api',
        apiError: (error as ApiError),
      })
    }
  }
);
// #endregion

type PhoneChangedAction = PayloadAction<string>;
type PasswordChangedAction = PayloadAction<string>;

export const authPhoneEnterSlice = createSlice({
  name: SLICE_NAME,
  initialState: { ...initialState },
  reducers: {
    phoneChanged: (state, action: PhoneChangedAction) => {
      state.phone = action.payload;
    },
    phoneErrorFixed: (state) => {
      state.phoneError = '';
    },
    passwordChanged: (state, action: PasswordChangedAction) => {
      state.password = action.payload;
    },
    reset: (state) => {
      state.phone = '';
      state.password = '';
      state.phoneError = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(keyLoginAsync.rejected, (state, action) => {
        switch (action.payload?.reason)
        {
          case 'validation':
            state.phoneError = action.payload.validationErrors?.phoneError ?? '';
            break;
        }
      });
  },
})

const selectPhone = (state: RootState) => state.authPhoneEnter.phone;
const selectPhoneError = (state: RootState) => state.authPhoneEnter.phoneError;
const selectPassword = (state: RootState) => state.authPhoneEnter.password;
const selectFormFilled = createSelector(
  [selectPhone, selectPassword],
  (phone, password) => {
    return (
      Util.selectDigitsFromString(phone).length > 1
      &&
      password.trim().length > 0
    );
  }
);

export const authPhoneEnterSelectors = {
  selectPhone,
  selectPhoneError,
  selectPassword,
  selectFormFilled,
}

export const authPhoneEnterActions = authPhoneEnterSlice.actions;

export default authPhoneEnterSlice.reducer;